import { useEffect } from 'react'

import { FormattedMessage } from 'react-intl'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { BrandFanGroupMemberWarningProvider } from '@context/BrandFanGroupMemberWarning'
import { IModalComponentProps } from '@context/Modal'
import { BrandFanFilters } from '@enums'
import { IApplicationState } from '@store'
import {
  fetchBrandFanGroups,
  selectBrandFanGroupsNextLink,
  selectBrandFanMember,
  selectIdentityFirstName,
  selectIdentityGroupIds,
  selectIsListEmpty,
} from '@store/brandFans'
import {
  selectBrandFanGroupsFromSubmission,
  selectIdentityFirstName as selectIdentityFirstNameFromSubmission,
} from '@store/submissions'
import { fetchBrandFansMembership } from '@store/submissions/actions/fetchSubmissionBrandFanMembership'
import { CloseIcon, Modal, ModalTitle } from '@tribegroup'
import BrandFansInfluencerIdentityGroupsEmpty from './Empty'
import BrandFansInfluencerIdentityFormAndWarningWrapper from './FormAndWarningWrapper'
import { MainContainer } from './Groups.styled'
import BrandFansInfluencerIdentityGroupsList from './List'

interface IProps extends IModalComponentProps {
  identityId: number
  brandFanMemberId?: number
  brandFanGroupId?: number
  influencerId?: number
  submissionId?: number
  campaignId?: number
}

const BrandFansInfluencerIdentityGroups: React.FC<IProps> = ({
  identityId,
  onRequestClose,
  isOpen = false,
  submissionId,
  campaignId,
  influencerId,
  brandFanGroupId,
  brandFanMemberId,
}) => {
  const dispatch = useDispatch()
  const { brandFanMember } = useSelector((state: IApplicationState) => ({
    brandFanMember:
      brandFanGroupId && brandFanGroupId
        ? selectBrandFanMember(state, brandFanGroupId, brandFanMemberId)
        : undefined,
  }), shallowEqual)
  const { isListEmpty, firstName, groupIds, hasNextLink } = useSelector(
    (state: IApplicationState) => {
      let groupIds = selectIdentityGroupIds(state, identityId)

      if (submissionId) {
        groupIds = selectBrandFanGroupsFromSubmission(state, submissionId)
      }
      return {
        isListEmpty: selectIsListEmpty(state, BrandFanFilters.Groups),
        firstName: submissionId
          ? selectIdentityFirstNameFromSubmission(state, submissionId)
          : selectIdentityFirstName(state, identityId),
        groupIds,
        hasNextLink: Boolean(selectBrandFanGroupsNextLink(state)),
      }
    },
    shallowEqual,
  )

  const onScroll = (event: React.UIEvent<HTMLElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5

    if (isAtBottom && hasNextLink) {
      dispatch(fetchBrandFanGroups(true))
    }
  }

  useEffect(() => {
    dispatch(fetchBrandFanGroups())
    if (submissionId) {
      dispatch(fetchBrandFansMembership(submissionId))
    }
  }, [])

  return (
    <Modal reactModalProps={{ isOpen }} width={27.5} complex>
      <BrandFanGroupMemberWarningProvider>
        <MainContainer data-testid="brand-fan-groups-modal">
          <CloseIcon onClick={onRequestClose} />
          <ModalTitle bottomOuterSpacing={1.25}>
            <FormattedMessage id="brandFans.identityGroups.title" />
          </ModalTitle>
          {isListEmpty ? (
            <BrandFansInfluencerIdentityGroupsEmpty />
          ) : (
            <BrandFansInfluencerIdentityGroupsList
              identityId={identityId}
              submissionId={submissionId}
              campaignId={campaignId}
              identityGroupIds={groupIds}
              influencerId={influencerId}
              onScroll={onScroll}
            />
          )}
          <BrandFansInfluencerIdentityFormAndWarningWrapper
            identityId={identityId}
            submissionId={submissionId}
            campaignId={campaignId}
            firstName={firstName ?? brandFanMember?.influencer_first_name}
            groupMembershipsCount={groupIds.length}
            influencerId={influencerId}
          />
        </MainContainer>
      </BrandFanGroupMemberWarningProvider>
    </Modal>
  )
}

export { BrandFansInfluencerIdentityGroups }
export default BrandFansInfluencerIdentityGroups
